import { Controller } from "stimulus";

export default class extends Controller {
  static targets = [
    "pointsField",
    "scaleTypeField",
    "headersSection",
    "headersRowTwo",
    "headerFieldTemplate",
    "headerField",
    "headerInput",
    "statementInput",
    "previewTable",
    "previewHeaderRow",
    "previewHeader",
    "previewHeaderTemplate",
    "previewRow",
    "previewCellTemplate"
  ];

  static values = {
    questionPartId: String,
    presetHeaders: Object
  };

  updateScaleType(event) {
    const scaleType = event.target.value;
    this.headersSectionTarget.hidden = scaleType !== 'custom';
    this._syncHeaderFieldCount();
    this._renderPreview();

    if (scaleType === 'custom' && this.headerInputTargets[0]) {
      this.headerInputTargets[0].focus();
    }
  }

  updatePoints(event) {
    this.pointsFieldTarget.value = event.detail.value;
    this._syncHeaderFieldCount();
    this._renderPreview();
    this._updateScaleTypeSubtitles();
    this._updatePreviewSpacing();
  }

  updateHeaderPreview() {
    this._renderPreview();
  }

  updateStatementPreview(event) {
    const index = this.statementInputTargets.indexOf(event.target);
    if (index === -1 || !this.previewRowTargets[index]) return;

    this.previewRowTargets[index]
      .querySelector('[data-test-grid-preview-statement]').textContent = event.target.value;
  }

  _currentScaleType() {
    return this.scaleTypeFieldTarget.querySelector('input').value;
  }

  _currentPoints() {
    return parseInt(this.pointsFieldTarget.value, 10);
  }

  _currentHeaders() {
    const scaleType = this._currentScaleType();

    if (scaleType === 'custom') {
      return this.headerInputTargets.map((input) => input.value || '');
    }

    return (this.presetHeadersValue[scaleType] || {})[this._currentPoints()] || [];
  }

  _updateScaleTypeSubtitles() {
    const points = this._currentPoints();

    this.scaleTypeFieldTarget.querySelectorAll('[data-value]').forEach((item) => {
      const headers = (this.presetHeadersValue[item.dataset.value] || {})[points];
      if (!headers) return;

      const subtitle = item.querySelector('.admin-dropdown-item-subtitle');
      if (subtitle) {
        subtitle.textContent = `${headers[0]} → ${headers[headers.length - 1]}`;
      }
    });
  }

  // Points is only ever 5 or 7, so the second header row only ever holds 2 or 4
  // fields - handled as two explicit states rather than a generic resize.
  _syncHeaderFieldCount() {
    const isCustom = this._currentScaleType() === 'custom';

    if (isCustom) {
      if (this._currentPoints() === 7 && this.headerFieldTargets.length === 5) {
        for (let i = 0; i < 2; i++) {
          this.headersRowTwoTarget.appendChild(
            this.headerFieldTemplateTarget.content.cloneNode(true)
          );
        }

        this.headersRowTwoTarget.classList.replace('grid-cols-2', 'grid-cols-4');
      } else if (this._currentPoints() === 5 && this.headerFieldTargets.length === 7) {
        for (let i = 0; i < 2; i++) {
          const fields = this.headerFieldTargets;
          fields[fields.length - 1].remove();
        }

        this.headersRowTwoTarget.classList.replace('grid-cols-4', 'grid-cols-2');
      }
    }

    const lastIndex = this.headerFieldTargets.length - 1;
    const middleIndex = Math.floor(this.headerFieldTargets.length / 2);
    const formErrorsController = this.application
      .getControllerForElementAndIdentifier(this.element.closest('form'), 'form-errors');

    this.headerFieldTargets.forEach((field, index) => {
      const input = field.querySelector('input');
      const wasRequired = input.required;
      input.id = `question_part_${this.questionPartIdValue}_headers_${index}`;
      input.required = isCustom && [0, middleIndex, lastIndex].includes(index);

      // Header is no longer first, middle or last - clear its stale required error.
      if (formErrorsController && wasRequired && !input.required) {
        formErrorsController.hideError(input);
      }

      const counter = field.querySelector('.js-grid-question-header-counter');
      if (counter) {
        counter.textContent = index + 1;
      }
    });
  }

  _renderPreview() {
    const headers = this._currentHeaders();

    this._resizeHeaderCells(headers.length);
    this.previewHeaderTargets.forEach((th, index) => {
      th.textContent = headers[index] || '';
    });

    this.previewRowTargets.forEach((row) => this._resizeRowCells(row, headers.length));
  }

  _resizeHeaderCells(count) {
    const delta = count - this.previewHeaderTargets.length;

    if (delta > 0) {
      for (let i = 0; i < delta; i++) {
        this.previewHeaderRowTarget.appendChild(
          this.previewHeaderTemplateTarget.content.cloneNode(true)
        );
      }
    } else {
      for (let i = 0; i < -delta; i++) {
        this.previewHeaderTargets[this.previewHeaderTargets.length - 1].remove();
      }
    }
  }

  _updatePreviewSpacing() {
    this.previewTableTarget.classList.toggle('is-seven-points', this._currentPoints() === 7);
  }

  _resizeRowCells(row, count) {
    const currentCount = row.querySelectorAll('td').length - 1;
    const delta = count - currentCount;

    if (delta > 0) {
      for (let i = 0; i < delta; i++) {
        row.appendChild(this.previewCellTemplateTarget.content.cloneNode(true));
      }
    } else {
      for (let i = 0; i < -delta; i++) {
        const cells = row.querySelectorAll('td');
        cells[cells.length - 1].remove();
      }
    }
  }
}
